/*
 * Directions asks which maps app to open, Apple Maps or Google Maps, so each worker uses the one they like (owner,
 * 2026-11-01). The last pick is kept on this device and shown first. With Don't ask again ticked, later taps open that app
 * straight away; the account menu's Directions item turns the question back on. Both links open the app on a phone when
 * it is there and the website when it is not.
 */
import { useState, type ReactNode } from "react";
import { splitAddress } from "@steward/shared";
import { Modal } from "./overlays";

export type MapsApp = "apple" | "google";
export interface MapsPick {
  app: MapsApp;
  /** Don't ask again: open this app without the question. */
  always: boolean;
}

const KEY = "steward.maps";
const enc = encodeURIComponent;
export const appleMapsTo = (address: string) => `https://maps.apple.com/?daddr=${enc(address)}`;
export const googleMapsTo = (address: string) => `https://www.google.com/maps/dir/?api=1&destination=${enc(address)}`;
const mapsTo = (app: MapsApp, address: string) => (app === "apple" ? appleMapsTo(address) : googleMapsTo(address));
export const MAPS_NAMES: Record<MapsApp, string> = { apple: "Apple Maps", google: "Google Maps" };
type Lang = "en" | "es";
const WORDS = {
  en: { title: "Open directions in", last: "Last used", always: "Don't ask again" },
  es: { title: "Abrir en", last: "Usado la última vez", always: "No volver a preguntar" },
};

export function readMapsPick(): MapsPick | null {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (v && typeof v === "object" && "app" in v && (v.app === "apple" || v.app === "google")) {
      return { app: v.app, always: "always" in v && v.always === true };
    }
  } catch {
    // Nothing saved or storage blocked: ask.
  }
  return null;
}

export function saveMapsPick(pick: MapsPick | null) {
  try {
    if (pick) localStorage.setItem(KEY, JSON.stringify(pick));
    else localStorage.removeItem(KEY);
  } catch {
    // Not saved; the question comes back next time.
  }
}

/** Opens directions straight in the saved app when Don't ask again is on. False: nothing opened, ask. */
export function openSavedDirections(address: string): boolean {
  const pick = readMapsPick();
  if (!pick?.always) return false;
  window.open(mapsTo(pick.app, address), "_blank", "noreferrer");
  return true;
}

/** The Apple Maps or Google Maps question for one address. Closed when address is null. */
export function DirectionsChoice({
  address,
  onClose,
  lang = "en",
}: {
  address: string | null;
  onClose: () => void;
  lang?: Lang;
}) {
  const w = WORDS[lang];
  const [always, setAlways] = useState(false);
  const last = readMapsPick()?.app;
  const apps: MapsApp[] = last === "google" ? ["google", "apple"] : ["apple", "google"];
  // Closed just after the tap, so the link still opens with the sheet gone.
  const pick = (app: MapsApp) => {
    saveMapsPick({ app, always });
    window.setTimeout(() => {
      setAlways(false);
      onClose();
    }, 0);
  };
  return (
    <Modal title={w.title} open={address !== null} onClose={onClose}>
      {address ? (
        <div className="maps-pick">
          {apps.map((app) => (
            <a
              key={app}
              className={app === last ? "btn primary" : "btn secondary"}
              href={mapsTo(app, address)}
              target="_blank"
              rel="noreferrer"
              onClick={() => pick(app)}
            >
              {MAPS_NAMES[app]}
              {app === last ? <span className="maps-last">{w.last}</span> : null}
            </a>
          ))}
          <label className="check-row">
            <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} />
            {w.always}
          </label>
        </div>
      ) : null}
    </Modal>
  );
}

/** A Directions button or link that asks Apple Maps or Google Maps first, unless Don't ask again is on. */
export function DirectionsButton({
  address,
  className = "btn secondary",
  lang = "en",
  children,
}: {
  address: string;
  className?: string;
  lang?: Lang;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          if (!openSavedDirections(address)) setOpen(true);
        }}
      >
        {children}
      </button>
      <DirectionsChoice address={open ? address : null} onClose={() => setOpen(false)} lang={lang} />
    </>
  );
}

/**
 * An address as people read it on a card: the street, then "New Orleans, LA 70114" under it. A line that does not split
 * (no city or state) stays as it was.
 */
export function AddressLines({ address }: { address: string }) {
  const p = splitAddress(address);
  if (!p.city) return <>{address}</>;
  return (
    <>
      {[p.street1, p.street2].filter(Boolean).join(", ")}
      <br />
      {p.city}, {[p.state, p.zip].filter(Boolean).join(" ")}
    </>
  );
}
